/**
 * HotelDetailPage — /hotels/:slug
 *
 * If the hotel has been enriched with our AI/AEO template
 * (``template_status ∈ {"enriched","owner_claimed"}``) we render our
 * own layout. Otherwise we fall back to embedding the Trivago listing
 * inside an <iframe>. Most Trivago/hotel sites send
 * ``X-Frame-Options: DENY`` so the iframe often renders blank — we
 * detect that via an onLoad timeout and reveal an outbound CTA card.
 *
 * Data source: GET /api/hotels/{slug}
 */
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, ExternalLink, Loader2, Globe, Bed,
  AlertCircle, Star, Phone, Send, CheckCircle2,
  Car, Wifi, Utensils, Waves, Wind, GlassWater, Shirt, Dog,
  Users, Zap as EvZap, Accessibility, Dumbbell, ChefHat, Waves as BeachIcon,
  Briefcase, ChevronDown, ChevronUp, Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL
  || (typeof window !== "undefined" ? window.location.origin : "");

// Time we wait for the iframe to signal "loaded" before we assume
// the target site blocked the embed and reveal the outbound CTA.
const IFRAME_TIMEOUT_MS = 3500;

export default function HotelDetailPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, error: "", data: null });
  // Community rating aggregate — kept out of the network state so
  // "Rate this stay" submissions can update it live without a refetch.
  // Must be declared before any early returns to keep hook order stable.
  const [agg, setAgg] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/hotels/${encodeURIComponent(slug)}`,
                                { credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (alive) {
          setState({ loading: false, error: "", data: d });
          setAgg(d.community_rating || null);
        }
      } catch (e) {
        if (alive) setState({
          loading: false,
          error: e.message || "Failed to load hotel",
          data: null,
        });
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        <PlatformHeader />
        <div className="max-w-4xl mx-auto p-8 text-center text-[#5C625E]"
              data-testid="hotel-detail-loading">
          <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />
          Loading hotel…
        </div>
      </div>
    );
  }
  if (state.error || !state.data) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        <PlatformHeader />
        <div className="max-w-4xl mx-auto p-8 text-center"
              data-testid="hotel-detail-error">
          <AlertCircle className="w-10 h-10 text-red-600 mx-auto mb-3" />
          <p className="font-semibold text-[#3A3F3B]">
            Couldn&apos;t load this hotel
          </p>
          <p className="text-sm text-[#5C625E] mb-4">{state.error}</p>
          <Link to="/roadtrip"
                className="text-[#C05746] hover:underline text-sm">
            ← Back to trip planner
          </Link>
        </div>
      </div>
    );
  }
  const { hotel, use_our_template } = state.data;
  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="hotel-detail-page">
      <PlatformHeader />
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        <Link to="/roadtrip"
              data-testid="hotel-detail-back"
              className="inline-flex items-center gap-1 text-sm text-[#5C625E]
                          hover:text-[#3A3F3B] mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to trip planner
        </Link>
        <HotelHero hotel={hotel} community={agg} />
        <AdminEnrichBar slug={hotel.slug || hotel.source_id}
                         status={state.data.template_status}
                         onEnriched={(fresh) => setState({
                           loading: false, error: "", data: fresh })} />
        {use_our_template
          ? <EnrichedTemplate data={state.data} />
          : <FallbackTemplate data={state.data} />}
        <RateThisStay
          slug={hotel.slug || hotel.source_id}
          hotelName={hotel.name}
          onSubmitted={(newAgg) => setAgg(newAgg)}
        />
      </div>
      <SuiteFooter trivagoContext="hotel" />
    </div>
  );
}

/* ─────────────────── Admin "Enrich this hotel" bar ─────────────────── */
function AdminEnrichBar({ slug, status, onEnriched }) {
  const { user } = useAuth() || {};
  const [busy, setBusy] = useState(false);
  const isAdmin = user && ["agency_owner", "admin"].includes(user.role);
  if (!isAdmin) return null;
  if (status === "enriched" || status === "owner_claimed") {
    return (
      <div className="mb-4 flex items-center gap-2 text-xs text-emerald-700
                       bg-emerald-50 border border-emerald-200 rounded-lg
                       px-3 py-2"
            data-testid="admin-enriched-badge">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Enriched · status: <b>{status}</b>
      </div>
    );
  }

  async function trigger() {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/hotels/${encodeURIComponent(slug)}/enrich`,
        { method: "POST", credentials: "include" });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${r.status}`);
      }
      toast.success("Enrichment complete — reloading");
      // Re-fetch the hotel so the UI switches templates.
      const g = await fetch(`${API}/api/hotels/${encodeURIComponent(slug)}`,
        { credentials: "include" });
      const fresh = await g.json();
      onEnriched(fresh);
    } catch (e) {
      toast.error(e?.message || "Enrichment failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 bg-[#FFF9EE]
                     border border-[#F0D97A] rounded-lg px-4 py-3"
          data-testid="admin-enrich-bar">
      <Sparkles className="w-4 h-4 text-[#B47613] shrink-0" />
      <span className="text-sm text-[#5C4A16]">
        Admin view · this hotel is <b>{status || "pending"}</b>.
        Trigger an AI/AEO enrichment to bypass the Trivago fallback.
      </span>
      <button type="button"
                onClick={trigger}
                disabled={busy}
                data-testid="admin-enrich-button"
                className="ml-auto inline-flex items-center gap-2
                            bg-[#C05746] hover:bg-[#A64A3C]
                            disabled:opacity-60 text-white text-sm
                            px-4 py-2 rounded-full font-medium">
        {busy
          ? <><Loader2 className="w-4 h-4 animate-spin" />Enriching…</>
          : <>Enrich this hotel</>}
      </button>
    </div>
  );
}

/* ─────────────────────────── shared bits ─────────────────────────── */

function HotelHero({ hotel, community }) {
  const addr = [hotel.suburb, hotel.state_code, hotel.postcode]
    .filter(Boolean).join(" ");
  const osmStars = parseFloat(hotel.stars) || 0;
  return (
    <header className="mb-5" data-testid="hotel-detail-hero">
      <div className="flex items-start gap-2 mb-1">
        <Bed className="w-6 h-6 text-[#C05746] mt-1 shrink-0" />
        <h1 className="text-2xl sm:text-3xl font-bold text-[#2A2F2B] leading-tight"
              data-testid="hotel-detail-name">
          {hotel.name}
        </h1>
      </div>
      {/* Rating badges — prefer HustleSuite community rating (proprietary
          data) when we have it, then fall back to OSM's `stars` tag. */}
      {community && community.count > 0 && (
        <div className="flex items-center gap-1.5 mb-1"
              data-testid="hotel-detail-community-rating"
              aria-label={`${community.avg} out of 5, ${community.count} reviews`}>
          {[...Array(5)].map((_, i) => (
            <Star key={i}
                  className={`w-4 h-4 ${
                    i < Math.floor(community.avg)
                      ? "fill-amber-400 text-amber-400"
                      : i < community.avg
                        ? "fill-amber-400/50 text-amber-400"
                        : "text-stone-300"}`} />
          ))}
          <span className="text-sm font-semibold text-[#3A3F3B] ml-0.5">
            {community.avg.toFixed(1)}
          </span>
          <span className="text-xs text-[#5C625E]">
            · {community.count} HustleSuite {community.count === 1 ? "review" : "reviews"}
          </span>
        </div>
      )}
      {!community?.count && osmStars > 0 && (
        <div className="flex items-center gap-0.5 mb-1"
              data-testid="hotel-detail-osm-stars"
              aria-label={`${osmStars}-star rating`}>
          {[...Array(Math.min(5, Math.floor(osmStars)))].map((_, i) => (
            <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
          ))}
          <span className="text-xs text-[#5C625E] ml-1">{osmStars}★</span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-4 text-sm text-[#5C625E]">
        {(addr || hotel.address) && (
          <span className="inline-flex items-center gap-1"
                data-testid="hotel-detail-address">
            <MapPin className="w-3.5 h-3.5" />
            {hotel.address || addr}
          </span>
        )}
        {hotel.phone && (
          <a href={`tel:${hotel.phone}`}
              data-testid="hotel-detail-phone"
              className="inline-flex items-center gap-1 hover:text-[#C05746]">
            <Phone className="w-3.5 h-3.5" /> {hotel.phone}
          </a>
        )}
        {hotel.website && (
          <a href={hotel.website} target="_blank" rel="noopener noreferrer"
              data-testid="hotel-detail-website"
              className="inline-flex items-center gap-1 hover:text-[#C05746]">
            <Globe className="w-3.5 h-3.5" /> Website
          </a>
        )}
      </div>
    </header>
  );
}

/* ─────────────────────── our own template ─────────────────────── */

// Structured amenity → lucide icon.
const AMENITY_ICONS = {
  parking:     Car,
  wifi:        Wifi,
  breakfast:   Utensils,
  pool:        Waves,
  aircon:      Wind,
  restaurant:  Utensils,
  bar:         GlassWater,
  laundry:     Shirt,
  pets:        Dog,
  family:      Users,
  ev_charger:  EvZap,
  accessible:  Accessibility,
  gym:         Dumbbell,
  kitchen:     ChefHat,
  beach:       BeachIcon,
  business:    Briefcase,
};

function EnrichedTemplate({ data }) {
  const { hotel, book_url, ai_content, schema_org, hero_photo_ref } = data;
  const ai = ai_content || {};

  // ── Hero image: prefer Google Places photo (proxied through our
  //    server so the API key stays private), else a warm gradient
  //    placeholder. Never break the layout if we have no image.
  const heroSrc = hero_photo_ref
    ? `${API}/api/hotels/photo/${encodeURIComponent(hero_photo_ref)}?w=1600`
    : null;

  return (
    <div data-testid="hotel-detail-enriched" className="space-y-6">
      {/* ─────────── Hero image ─────────── */}
      {heroSrc ? (
        <div className="relative rounded-xl overflow-hidden bg-[#E8E5DF]"
              data-testid="hotel-detail-hero-image"
              style={{ aspectRatio: "16 / 9" }}>
          <img src={heroSrc} alt={hotel.name}
                loading="eager"
                className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="rounded-xl bg-gradient-to-br from-[#F7F3E8]
                          to-[#EEE7D3] flex items-center justify-center"
              data-testid="hotel-detail-hero-placeholder"
              style={{ aspectRatio: "16 / 9" }}>
          <Bed className="w-16 h-16 text-[#C05746]/60" />
        </div>
      )}

      {/* ─────────── AI summary ─────────── */}
      {ai.summary && (
        <section data-testid="hotel-detail-summary"
                  className="bg-white border border-[#E8E5DF] rounded-xl p-5 sm:p-6">
          <p className="text-[15px] leading-[1.7] text-[#3A3F3B] whitespace-pre-line">
            {ai.summary}
          </p>
        </section>
      )}

      {/* ─────────── Highlights strip ─────────── */}
      {Array.isArray(ai.highlights) && ai.highlights.length > 0 && (
        <section data-testid="hotel-detail-highlights"
                  className="bg-white border border-[#E8E5DF] rounded-xl p-5">
          <h2 className="text-lg font-semibold text-[#2A2F2B] mb-3">
            What guests love
          </h2>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {ai.highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#3A3F3B]">
                <CheckCircle2 className="w-4 h-4 mt-[3px] text-emerald-600 shrink-0" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ─────────── Amenities grid ─────────── */}
      {Array.isArray(ai.amenities) && ai.amenities.length > 0 && (
        <section data-testid="hotel-detail-amenities"
                  className="bg-white border border-[#E8E5DF] rounded-xl p-5">
          <h2 className="text-lg font-semibold text-[#2A2F2B] mb-3">
            Amenities
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {ai.amenities.map((a, i) => {
              const Icon = AMENITY_ICONS[a.key] || CheckCircle2;
              return (
                <div key={i}
                      data-testid={`amenity-${a.key || i}`}
                      className="flex items-center gap-2 text-sm
                                  text-[#3A3F3B] bg-[#FAF8F3] rounded-lg
                                  px-3 py-2">
                  <Icon className="w-4 h-4 text-[#C05746] shrink-0" />
                  <span>{a.label}</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ─────────── Nearby hints ─────────── */}
      {Array.isArray(ai.nearby_hints) && ai.nearby_hints.length > 0 && (
        <section data-testid="hotel-detail-nearby"
                  className="bg-white border border-[#E8E5DF] rounded-xl p-5">
          <h2 className="text-lg font-semibold text-[#2A2F2B] mb-3">
            What&apos;s nearby
          </h2>
          <ul className="space-y-2">
            {ai.nearby_hints.map((n, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 mt-[3px] text-[#C05746] shrink-0" />
                <div>
                  <span className="font-medium text-[#2A2F2B]">{n.name}</span>
                  {n.note && (
                    <span className="text-[#5C625E]"> — {n.note}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ─────────── Book CTA ─────────── */}
      <div className="flex justify-center pt-2">
        <a
          href={book_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hotel-detail-book-cta"
          className="inline-flex items-center gap-2 bg-[#C05746]
                      hover:bg-[#A64A3C] text-white px-7 py-3 rounded-full
                      font-semibold shadow-sm">
          Check availability &amp; book <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* ─────────── FAQ accordion (AEO-tuned) ─────────── */}
      {Array.isArray(ai.faqs) && ai.faqs.length > 0 && (
        <section data-testid="hotel-detail-faq"
                  className="bg-white border border-[#E8E5DF] rounded-xl p-5">
          <h2 className="text-lg font-semibold text-[#2A2F2B] mb-3">
            Frequently asked
          </h2>
          <div className="divide-y divide-[#E8E5DF]">
            {ai.faqs.map((f, i) => (
              <FaqRow key={i} q={f.q} a={f.a} idx={i} />
            ))}
          </div>
        </section>
      )}

      {/* ─────────── schema.org JSON-LD (AEO for AI engines) ─────────── */}
      {schema_org && (
        <script
          type="application/ld+json"
          data-testid="hotel-detail-jsonld"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema_org) }}
        />
      )}
    </div>
  );
}

function FaqRow({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);  // first Q expanded by default
  return (
    <div className="py-3">
      <button type="button"
              onClick={() => setOpen((s) => !s)}
              data-testid={`hotel-faq-toggle-${idx}`}
              aria-expanded={open}
              className="w-full flex items-center justify-between gap-3
                          text-left font-medium text-[#2A2F2B] hover:text-[#C05746]">
        <span>{q}</span>
        {open
          ? <ChevronUp className="w-4 h-4 shrink-0" />
          : <ChevronDown className="w-4 h-4 shrink-0" />}
      </button>
      {open && (
        <p className="mt-2 text-sm text-[#5C625E] leading-relaxed">
          {a}
        </p>
      )}
    </div>
  );
}

/* ──────────────── Trivago / hotel-site iframe fallback ─────────────── */

function FallbackTemplate({ data }) {
  const { hotel, book_url, primary_url, primary_target } = data;
  // Timeline:
  //   1. mount <iframe> at primary_url (hotel website if we have it, else Trivago)
  //   2. if onLoad fires within 3.5s → iframe is fine, hide the fallback
  //   3. if it doesn't → assume X-Frame-Options blocked us; reveal CTA
  const [embedFailed, setEmbedFailed] = useState(false);
  const [embedLoaded, setEmbedLoaded] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // Only meaningful when we actually try the iframe.
    if (!primary_url) return;
    timerRef.current = setTimeout(() => {
      if (!embedLoaded) setEmbedFailed(true);
    }, IFRAME_TIMEOUT_MS);
    return () => clearTimeout(timerRef.current);
  }, [primary_url, embedLoaded]);

  const onIframeLoad = () => {
    setEmbedLoaded(true);
    clearTimeout(timerRef.current);
  };

  return (
    <div className="space-y-4" data-testid="hotel-detail-fallback">
      {/* Outbound CTA — always visible so users have a working path.
          The Trivago listing embeds directly below (or an outbound
          card if the target site blocks framing). No internal notice
          because users shouldn't see the seams. */}
      <div className="flex flex-wrap gap-2">
        {hotel.website && primary_target !== "hotel" && (
          <a href={hotel.website} target="_blank" rel="noopener noreferrer"
              data-testid="hotel-detail-open-website"
              className="inline-flex items-center gap-2 bg-white border
                          border-[#C05746] text-[#C05746] hover:bg-[#FCEEE9]
                          px-4 py-2 rounded-md font-semibold text-sm">
            <Globe className="w-4 h-4" /> Visit hotel website
          </a>
        )}
        <a
          href={book_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hotel-detail-book-cta"
          className="inline-flex items-center gap-2 bg-[#C05746]
                      hover:bg-[#A64A3C] text-white px-5 py-2 rounded-md
                      font-semibold text-sm">
          {primary_target === "hotel" ? "Compare prices on Trivago" : "Book a room"}
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {primary_url && (
        <div className="relative bg-white border border-[#E8E5DF] rounded-lg
                         overflow-hidden shadow-sm"
              style={{ minHeight: "82vh" }}
              data-testid="hotel-detail-embed-frame">
          {embedFailed && (
            <div className="absolute inset-0 flex items-center justify-center
                             p-8 text-center bg-[#FAF8F3]"
                  data-testid="hotel-detail-embed-blocked">
              <div className="max-w-md">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full
                                 bg-[#C05746]/10 flex items-center justify-center">
                  <ExternalLink className="w-6 h-6 text-[#C05746]" />
                </div>
                <p className="text-base font-semibold text-[#3A3F3B] mb-1">
                  Continue on {primary_target === "hotel" ? "the hotel's website" : "Trivago"}
                </p>
                <p className="text-sm text-[#5C625E] mb-4">
                  This booking site opens in a new tab. Your search
                  and rating are saved here.
                </p>
                <a href={primary_url} target="_blank" rel="noopener noreferrer"
                    data-testid="hotel-detail-embed-open"
                    className="inline-flex items-center gap-2 bg-[#C05746]
                                hover:bg-[#A64A3C] text-white px-5 py-2.5
                                rounded-md text-sm font-semibold">
                  Open {primary_target === "hotel" ? "hotel website" : "Trivago listing"}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          )}
          {!embedFailed && !embedLoaded && (
            <div className="absolute inset-0 flex items-center justify-center
                             text-[#9BA19C]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading availability…
            </div>
          )}
          <iframe
            src={primary_url}
            title={hotel.name}
            data-testid="hotel-detail-iframe"
            onLoad={onIframeLoad}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            referrerPolicy="no-referrer-when-downgrade"
            loading="lazy"
            className="w-full h-full absolute inset-0"
          />
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── Rate this stay ─────────────────────── */

/**
 * Guest review widget — 1-5 star picker + optional comment box.
 * Every submission increments a Mongo aggregate so the hotel's
 * community rating shows up in the hero on next load. Rate-limited
 * to 3/day per IP (backend). Anonymous by default; name optional.
 */
function RateThisStay({ slug, hotelName, onSubmitted }) {
  const [stars,   setStars]   = useState(0);
  const [hover,   setHover]   = useState(0);
  const [comment, setComment] = useState("");
  const [author,  setAuthor]  = useState("");
  const [busy,    setBusy]    = useState(false);
  const [done,    setDone]    = useState(false);
  const [error,   setError]   = useState("");

  async function submit(e) {
    e.preventDefault();
    if (stars < 1) {
      setError("Pick a star rating first"); return;
    }
    setBusy(true); setError("");
    try {
      const r = await fetch(
        `${API}/api/hotels/${encodeURIComponent(slug)}/reviews`,
        {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ stars, comment, author }),
        });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setDone(true);
      if (data.community_rating && onSubmitted)
        onSubmitted(data.community_rating);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="mt-6 p-5 bg-emerald-50 border border-emerald-200 rounded-lg
                       flex items-start gap-3"
            data-testid="hotel-detail-review-thanks">
        <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
        <div>
          <p className="font-semibold text-emerald-900">Thanks for the review!</p>
          <p className="text-sm text-emerald-800">
            Your {stars}-star rating for {hotelName} helps other travellers.
          </p>
        </div>
      </div>
    );
  }
  return (
    <form
      onSubmit={submit}
      className="mt-6 p-5 bg-white border border-[#E8E5DF] rounded-lg"
      data-testid="hotel-detail-rate-form"
    >
      <h2 className="font-semibold text-[#2A2F2B] mb-1">Rate this stay</h2>
      <p className="text-xs text-[#5C625E] mb-3">
        Been here? Leave a rating — helps other travellers and builds
        HustleSuite&apos;s independent review database.
      </p>
      <div className="flex items-center gap-1 mb-3">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`hotel-detail-star-${n}`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => setStars(n)}
            className="p-0.5 focus:outline-none focus:ring-2 focus:ring-amber-400 rounded"
            aria-label={`${n} stars`}
          >
            <Star
              className={`w-7 h-7 transition-colors ${
                (hover || stars) >= n
                  ? "fill-amber-400 text-amber-400"
                  : "text-stone-300"}`}
            />
          </button>
        ))}
        {stars > 0 && (
          <span className="ml-2 text-sm font-medium text-[#3A3F3B]">
            {stars} / 5
          </span>
        )}
      </div>
      <input
        type="text"
        placeholder="Your name (optional)"
        value={author}
        onChange={(e) => setAuthor(e.target.value)}
        maxLength={80}
        data-testid="hotel-detail-review-author"
        className="w-full mb-2 px-3 py-2 rounded border border-[#E8E5DF] text-sm
                    focus:outline-none focus:ring-2 focus:ring-[#C05746]/40"
      />
      <textarea
        placeholder="Tell us about your stay (optional)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={3}
        maxLength={500}
        data-testid="hotel-detail-review-comment"
        className="w-full mb-2 px-3 py-2 rounded border border-[#E8E5DF] text-sm
                    focus:outline-none focus:ring-2 focus:ring-[#C05746]/40 resize-none"
      />
      {error && (
        <p className="text-xs text-red-600 mb-2"
            data-testid="hotel-detail-review-error">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={busy || stars < 1}
        data-testid="hotel-detail-review-submit"
        className="inline-flex items-center gap-2 bg-[#C05746] hover:bg-[#A64A3C]
                    disabled:bg-stone-300 disabled:cursor-not-allowed
                    text-white px-4 py-2 rounded-md font-semibold text-sm"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {busy ? "Submitting…" : "Submit review"}
      </button>
    </form>
  );
}
